/**
 * Header component that is used globally with it's default props
 */
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { AppFeatures } from 'components/GrowthBookWrapper/GrowthBookWrapper';
import { getRouteLanguages, INDEX, PRODUCTS, SHOPS } from 'config/fe-routes';
import { useRouter } from 'next/router';
import { createRef, ReactElement, RefObject, useEffect, useState } from 'react';
import IosHeart from 'react-ionicons/lib/IosHeart';
import adjustMainContainerOffset from 'utils/header-margin';
import { redirectTo } from 'utils/route-helpers';
import { useIsScreenSizeMax } from 'utils/screen-size';

import { saveLikedProducts } from '../../store/actions/products-page.action-types';
import { saveShopsPageFeaturedUsers, saveShopsPageLikedUsers } from '../../store/actions/shops-page.action-types';
import { LanguageDropdown } from '../LanguageDropdown/LanguageDropdown';
import HeaderGlobalSearch from './header-items/HeaderGlobalSearch/HeaderGlobalSearch';
import HeaderIconLogo from './header-items/HeaderIconLogo';
import HeaderItem from './header-items/HeaderItem';
import HeaderLogin from './header-items/HeaderLogin/HeaderLogin';
import HeaderTabs from './header-items/HeaderTabs/HeaderTabs';
import { HeaderBar } from './HeaderBar';

export type HeaderProps = {
  children?: ReactElement | Array<ReactElement | ReactElement[]>;
  mobileChildren?: Array<ReactElement | ReactElement[]>;
  visible?: boolean;
  className?: string;
  id?: string;
  innerRef?: RefObject<any>;
  disableOffsetAdjustment?: boolean;
  position?: 'fixed' | 'sticky' | 'static' | 'absolute' | 'relative';
};

const Header = (props: HeaderProps) => {
  const { mobileChildren, visible, className, id, innerRef, disableOffsetAdjustment, position } = props;
  let { children } = props;
  const [, /* headerHeight */ setHeaderHeight] = useState(0);
  const { route } = useRouter();
  const isMobileScreen = useIsScreenSizeMax('md');
  const canChangeLanguage = useFeatureIsOn<AppFeatures>('can_change_language');

  const headerVisible = visible !== undefined ? visible : true;

  // Header reference
  const headerRef = createRef<HTMLElement>();
  const currentHeaderRef = innerRef || headerRef;

  // Selected tab
  const routeLanguages = getRouteLanguages();
  const tabs = routeLanguages.reduce(
    (allTabs, routeLanguageCode) => ({
      ...allTabs,
      [INDEX().href]: 0,
      [SHOPS(routeLanguageCode).href]: 1,
      [PRODUCTS(routeLanguageCode).href]: 2,
    }),
    {},
  );
  const tab = tabs[route];

  // Handle tab change
  const handleTabChange = (newValue: number) => {
    if (newValue === 0) {
      redirectTo(INDEX());
    } else if (newValue === 1) {
      redirectTo(SHOPS());
    } else if (newValue === 2) {
      redirectTo(PRODUCTS());
    }
  };

  // Apply correct padding to main-container based on header height
  useEffect(() => {
    if (currentHeaderRef?.current) {
      adjustMainContainerOffset(currentHeaderRef, 'main-container', setHeaderHeight, disableOffsetAdjustment);
    }
  }, [currentHeaderRef, disableOffsetAdjustment]);

  // Global header defaults

  const headerIcon = <HeaderIconLogo icon="home" />;
  const headerSearch = <HeaderGlobalSearch />;
  const headerTabs = (
    <HeaderTabs
      selectedTab={tab}
      tabs={[
        {
          label: 'pages.index.title',
        },
        {
          label: 'pages.shops.title',
          icon: <IosHeart />,
          click: () => {
            // Invalidate caches.
            saveShopsPageFeaturedUsers([]);
            saveShopsPageLikedUsers([]);
          },
        },
        {
          label: 'pages.products.title',
          icon: <IosHeart />,
          click: () => {
            // Invalidate cache.
            saveLikedProducts([]);
          },
        },
      ]}
      onTabChange={(newTab) => handleTabChange(newTab)}
    />
  );
  const headerLogin = <HeaderLogin />;
  const headerSpacing = <HeaderItem className="flex-grow" />;
  const languageDropdown = canChangeLanguage ? <LanguageDropdown className="ml-1" /> : null;

  children =
    children ||
    (!isMobileScreen
      ? [[headerIcon, headerSearch, headerTabs, headerLogin, languageDropdown]]
      : [[headerIcon, headerSpacing, headerLogin], headerSearch, headerTabs]);

  // Desktop vs mobile children

  let headerChildren = isMobileScreen ? mobileChildren || children : children;

  headerChildren = Array.isArray(headerChildren) ? headerChildren : [headerChildren];

  const headerBarProps = {
    headerChildren,
    className,
    currentHeaderRef,
    id,
    position,
  };

  return <>{headerVisible && <HeaderBar {...headerBarProps} />}</>;
};

export default Header;
